/* -------------------------- Design imports start -------------------------- */
import { Box, Breadcrumbs, Grid, Link, Typography } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement } from "react";

import ErrorBoundary from './ErrorBoundry'
import useMobileService from '../../hooks/useMobileService'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  children: React.ReactNode
  title?: React.ReactNode
  showTitleOnMobile?: boolean
  breadcrumbs?: { name: string; href?: string }[]
  className?: string
  marginBottom?: string
  withoutPadding?: boolean
  customMaxContentWidth?: string
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function Page(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const isMobile = useMobileService()
  const {
    children,
    title,
    breadcrumbs,
    className,
    showTitleOnMobile,
    marginBottom,
    withoutPadding,
    customMaxContentWidth,
  } = props
  const sidebarSpace = isMobile ? '0px' : 'var(--sidebar-width, 0px)'
  const infoPanelSpace = 'var(--info-panel-width, 0px)'
  const infoPanelOffset = isMobile ? '0px' : infoPanelSpace
  const mainContentWidth = isMobile ? '100%' : `calc(100% - ${sidebarSpace} - ${infoPanelOffset})`
  const maxContentWidth = customMaxContentWidth || (isMobile ? '100vw' : `calc(95vw - ${sidebarSpace} - ${infoPanelOffset})`)
  const layoutTransition = '0.3s ease'
  /* --------------------------- Non state data end --------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Box
      flex={1}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        marginBottom: marginBottom,
      }}
    >
      <Box
        flex={1}
        sx={{
          width: mainContentWidth,
          maxWidth: maxContentWidth,
          padding: withoutPadding ? 0 : '10px',
          position: 'relative',
          marginLeft: 0,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: `padding-right ${layoutTransition}, width ${layoutTransition}, max-width ${layoutTransition}`,
        }}
      >
        <Grid style={{ height: '98%' }} container className={className || 'main'}>
          <Grid container className="header">
            <Grid item>
              {title &&
                (showTitleOnMobile || !isMobile) &&
                (typeof title === 'string' ? <h2 className="title">{title}</h2> : title)}
            </Grid>
            <Grid item>
              {breadcrumbs && (
                <Breadcrumbs maxItems={2} aria-label="breadcrumb">
                  {breadcrumbs.map(({ name, href }, index) =>
                    index < breadcrumbs.length - 1 ? (
                      <Link color="inherit" key={href} href={href}>
                        {name}
                      </Link>
                    ) : (
                      <Typography color="textPrimary" key={href}>{name}</Typography>
                    )
                  )}
                </Breadcrumbs>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <ErrorBoundary>{children}</ErrorBoundary>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
